import { useEffect } from 'react';

import usePublicApiQuery from '@/hooks/api/usePublicApiQuery';
import { isBuildFinished } from '@/models/Build';
import { SimpleErrorResponse } from '@/models/Error';
import { LogFormat } from '@/models/LogFormat';
import { buildLogSummaryPath } from '@/utils/publicApiRouteService';
import { defaultQueryRetryCount } from '@/utils/query';

import useBuildDetailsPageStore, { BuildLogSummaryStatus, updateLogSummaryInfo } from '../BuildDetailsPage.store';
import { BuildLogSummaryQueryKeys } from '../components/StructuredBuildLog/utils/BuildLogQueryKeys';
import BuildLogSummary, { hasSummaryWorkflows } from '../types/BuildLogSummary';

const useBuildLogSummary = ({
  appSlug,
  buildSlug,
  enabled,
  isViewedAsPublicPage,
}: {
  appSlug: string;
  buildSlug: string;
  enabled: boolean;
  isViewedAsPublicPage: boolean;
}) => {
  const initBuild = useBuildDetailsPageStore((state) => state.initBuild);
  const canViewBuildLogs = useBuildDetailsPageStore((state) => state.abilities.canViewBuildLogs);

  const result = usePublicApiQuery<BuildLogSummary, SimpleErrorResponse>(
    BuildLogSummaryQueryKeys.detail(buildSlug),
    buildLogSummaryPath(appSlug, buildSlug),
    {
      refetchOnWindowFocus: false,
      requestConfig: {
        publicAccess: isViewedAsPublicPage,
      },
      select: (resp) => resp.data,
      enabled: (canViewBuildLogs || isViewedAsPublicPage) && enabled,
      retry: (count, error) => {
        const status = error?.response?.status;
        return status !== 404 && status !== 403 && count < defaultQueryRetryCount;
      },
    },
  );

  const hasBuildEnvironmentSetupLogs = !!result.data?.hasBuildEnvironmentSetupLogs;

  const showStructuredBuildLog =
    initBuild.logFormat === LogFormat.JSON &&
    isBuildFinished(initBuild) &&
    Boolean(result.data?.isLogArchived) &&
    (hasSummaryWorkflows(result.data) || hasBuildEnvironmentSetupLogs);
  const { status, isFetching, error } = result;
  const isLogDeleted = error?.response?.status === 404;

  useEffect(() => {
    let summaryStatus: BuildLogSummaryStatus = status;
    if (isFetching) {
      summaryStatus = 'fetching';
    } else if (isLogDeleted) {
      summaryStatus = 'deleted';
    }
    updateLogSummaryInfo({ logSummary: result.data, logSummaryStatus: summaryStatus });
  }, [status, isLogDeleted, isFetching, result.data]);

  return {
    ...result,
    showStructuredBuildLog,
    summaryStatus: status,
  };
};

export default useBuildLogSummary;
